import { useEffect, useState } from "react";
import 'chart.js/auto';
import { Bar, Line } from "react-chartjs-2";
import {
    FaChartLine,
    FaMoneyBillWave,
    FaFileInvoiceDollar,
    FaArrowUp,
    FaArrowDown,
    FaExclamationTriangle,
    FaChartBar
} from "react-icons/fa";
import {
    getMonthlyLoanDistribution, getMonthlyLoanDisbursement, getAverageTicketSize, getMoMGrowth, getEMIBounceRate,
    getMonthlyLoanDisbursementPerBranch
} from "../../Api/Api";
import { useWorkshop } from "../Common/WorkshopContext";
import { useNotification } from "../Notification/NotificationContext";

const FinanceReports = () => {
    const { workshopId } = useWorkshop();
    const [monthlyDistributionData, setMonthlyDistributionData] = useState(null);
    const [monthlyDisbursementData, setMonthlyDisbursementData] = useState(null);
    const [averageTicketSizeData, setAverageTicketSizeData] = useState(null);
    const [branchDisbursementData, setBranchDisbursementData] = useState(null);
    const [summaryStats, setSummaryStats] = useState({
        totalDisbursed: 0,
        totalLoans: 0,
        avgLoanAmount: 0
    });
    const [momGrowthData, setMomGrowthData] = useState(null);
    const [emiBounceRateData, setEmiBounceRateData] = useState(null);
    const { showNotification } = useNotification();

    useEffect(() => {
        fetchMonthlyDistribution();
        fetchMonthlyDisbursement();
        fetchAverageTicketSize();
        fetchMoMGrowth();
        fetchEMIBounceRate();
    }, [workshopId]);

    const fetchMonthlyDistribution = async () => {
        try {
            const response = await getMonthlyLoanDistribution(workshopId, 12);
            const data = response.data;

            if (!data || data.length === 0) {
                showNotification("No finance data available", "warning");
                return;
            }

            // Calculate summary statistics
            const totalDisbursed = data.reduce((sum, item) => sum + parseFloat(item.totalLoanAmount), 0);
            const totalLoans = data.reduce((sum, item) => sum + item.loanCount, 0);
            const avgLoanAmount = totalLoans > 0 ? totalDisbursed / totalLoans : 0;

            setSummaryStats({
                totalDisbursed,
                totalLoans,
                avgLoanAmount
            });

            // Prepare chart data - reverse to show oldest to newest
            const reversedData = [...data].reverse();
            const months = reversedData.map(d => {
                const [year, month] = d.month.split('-');
                return `${month}/${year}`;
            });
            const amounts = reversedData.map(d => parseFloat(d.totalLoanAmount));

            setMonthlyDistributionData({
                labels: months,
                datasets: [{
                    label: "Monthly Disbursements (₹)",
                    data: amounts,
                    backgroundColor: "#10B981",
                    borderColor: "#059669",
                    borderWidth: 1
                }]
            });
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const fetchMonthlyDisbursement = async () => {
        try {
            const response = await getMonthlyLoanDisbursement(workshopId, 12);
            const data = response.data;

            if (!data || data.length === 0) {
                return;
            }

            // Prepare chart data - reverse to show oldest to newest
            const reversedData = [...data].reverse();
            const months = reversedData.map(d => {
                const [year, month] = d.month.split('-');
                return `${month}/${year}`;
            });
            const counts = reversedData.map(d => d.loansDisbursed);

            setMonthlyDisbursementData({
                labels: months,
                datasets: [{
                    label: "Number of Loans",
                    data: counts,
                    backgroundColor: "#3B82F6",
                    borderColor: "#2563EB",
                    borderWidth: 2,
                    fill: false,
                    tension: 0.4
                }]
            });
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const fetchAverageTicketSize = async () => {
        try {
            const response = await getAverageTicketSize(workshopId, 12);
            const data = response.data;

            if (!data || data.length === 0) {
                return;
            }

            // Prepare chart data - reverse to show oldest to newest
            const reversedData = [...data].reverse();
            const months = reversedData.map(d => {
                const [year, month] = d.month.split('-');
                return `${month}/${year}`;
            });
            const avgAmounts = reversedData.map(d => parseFloat(d.averageTicketSize));

            setAverageTicketSizeData({
                labels: months,
                datasets: [{
                    label: "Average Ticket Size (₹)",
                    data: avgAmounts,
                    backgroundColor: "#8B5CF6",
                    borderColor: "#7C3AED",
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4
                }]
            });
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const fetchMoMGrowth = async () => {
        try {
            const response = await getMoMGrowth(workshopId);
            setMomGrowthData(response.data);
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const fetchEMIBounceRate = async () => {
        try {
            const response = await getEMIBounceRate(workshopId);
            setEmiBounceRateData(response.data);
        } catch (error) {
            showNotification(error.message, "error");
        }
    };
    useEffect(() => {
        fetchBranchDisbursement();
    }, [workshopId]);

    const fetchBranchDisbursement = async () => {
        try {
            const response = await getMonthlyLoanDisbursementPerBranch(workshopId); // API returns array [{ branchName, totalDisbursed }]
            const data = response.data;

            if (!data || data.length === 0) return;

            const branchNames = data.map(d => d.branchName);
            const amounts = data.map(d => parseFloat(d.totalDisbursed));

            setBranchDisbursementData({
                labels: branchNames,
                datasets: [{
                    label: "Loan Disbursement (₹)",
                    data: amounts,
                    backgroundColor: branchNames.map(() => `rgba(${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)}, 0.6)`),
                    borderColor: branchNames.map(() => `rgba(0,0,0,0.8)`),
                    borderWidth: 1
                }]
            });
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const branchChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `₹${context.parsed.y.toLocaleString('en-IN')}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function(value) {
                        return '₹' + value.toLocaleString('en-IN');
                    }
                }
            }
        }
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `₹${context.parsed.y.toLocaleString('en-IN')}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function(value) {
                        return '₹' + value.toLocaleString('en-IN');
                    }
                }
            }
        }
    };

    const loanCountChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `Loans: ${context.parsed.y}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1
                }
            }
        }
    };

    return (
        <div className="max-w-screen-xl mx-auto mt-6">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-4 rounded-t-lg">
                    <h2 className="text-2xl font-semibold text-center flex items-center justify-center gap-2">
                        <FaChartLine /> Finance Reports
                    </h2>
                </div>

                {/* Summary Statistics Cards */}
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 shadow-md p-6 rounded-lg flex items-center">
                        <FaMoneyBillWave className="text-green-500 text-3xl mr-4" />
                        <div>
                            <p className="text-gray-600 text-sm font-medium">Total Disbursed (12 months)</p>
                            <p className="text-2xl font-bold text-green-600">
                                ₹{summaryStats.totalDisbursed.toLocaleString('en-IN')}
                            </p>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 shadow-md p-6 rounded-lg flex items-center">
                        <FaFileInvoiceDollar className="text-blue-500 text-3xl mr-4" />
                        <div>
                            <p className="text-gray-600 text-sm font-medium">Total Loans Disbursed</p>
                            <p className="text-2xl font-bold text-blue-600">
                                {summaryStats.totalLoans}
                            </p>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 shadow-md p-6 rounded-lg flex items-center">
                        <FaChartLine className="text-purple-500 text-3xl mr-4" />
                        <div>
                            <p className="text-gray-600 text-sm font-medium">Average Loan Amount</p>
                            <p className="text-2xl font-bold text-purple-600">
                                ₹{summaryStats.avgLoanAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                            </p>
                        </div>
                    </div>

                    {/* MoM Growth Card */}
                    {momGrowthData && (
                        <div className={`bg-gradient-to-br shadow-md p-6 rounded-lg ${
                            parseFloat(momGrowthData.growthPercentageAnnualized) >= 0
                                ? 'from-emerald-50 to-emerald-100'
                                : 'from-red-50 to-red-100'
                        }`}>
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <p className="text-gray-600 text-sm font-medium mb-1">MoM Growth (Annualized)</p>
                                    <div className="flex items-center gap-2">
                                        <p className={`text-2xl font-bold ${
                                            parseFloat(momGrowthData.growthPercentageAnnualized) >= 0
                                                ? 'text-emerald-600'
                                                : 'text-red-600'
                                        }`}>
                                            {parseFloat(momGrowthData.growthPercentageAnnualized).toFixed(2)}%
                                        </p>
                                        {parseFloat(momGrowthData.growthPercentageAnnualized) >= 0 ? (
                                            <FaArrowUp className="text-emerald-500 text-xl" />
                                        ) : (
                                            <FaArrowDown className="text-red-500 text-xl" />
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Current AUM: ₹{parseFloat(momGrowthData.currentAUM).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Charts Section - First Row */}
                <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Monthly Disbursements Chart */}
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                        <h3 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                            <FaMoneyBillWave className="text-green-500" />
                            Monthly Disbursements (Last 12 Months)
                        </h3>
                        {monthlyDistributionData ? (
                            <div className="h-96">
                                <Bar data={monthlyDistributionData} options={chartOptions} />
                            </div>
                        ) : (
                            <div className="h-96 flex items-center justify-center text-gray-400">
                                <p>Loading chart data...</p>
                            </div>
                        )}
                    </div>

                    {/* Number of Loans Disbursed Chart */}
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                        <h3 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                            <FaFileInvoiceDollar className="text-blue-500" />
                            Number of Loans Disbursed (Last 12 Months)
                        </h3>
                        {monthlyDisbursementData ? (
                            <div className="h-96">
                                <Line data={monthlyDisbursementData} options={loanCountChartOptions} />
                            </div>
                        ) : (
                            <div className="h-96 flex items-center justify-center text-gray-400">
                                <p>Loading chart data...</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Average Ticket Size Chart - Second Row */}
                <div className="px-6 pb-6">
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                        <h3 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                            <FaChartLine className="text-purple-500" />
                            Average Ticket Size (Last 12 Months)
                        </h3>
                        {averageTicketSizeData ? (
                            <div className="h-96">
                                <Line data={averageTicketSizeData} options={chartOptions} />
                            </div>
                        ) : (
                            <div className="h-96 flex items-center justify-center text-gray-400">
                                <p>Loading chart data...</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* EMI Bounce Rate Section - Third Row */}
                {emiBounceRateData && (
                    <div className="px-6 pb-6">
                        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                            <h3 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                                <FaExclamationTriangle className="text-orange-500" />
                                EMI Collection Efficiency (Last 3 Months)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Bounced EMIs Card */}
                                <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-lg border-l-4 border-red-500">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600 font-medium mb-1">Bounced EMIs</p>
                                            <p className="text-3xl font-bold text-red-600">{emiBounceRateData.bouncedEMIs}</p>
                                            <p className="text-xs text-gray-500 mt-1">Overdue or Partially Paid</p>
                                        </div>
                                        <FaExclamationTriangle className="text-red-400 text-4xl opacity-50" />
                                    </div>
                                </div>

                                {/* Total EMIs Due Card */}
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg border-l-4 border-blue-500">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600 font-medium mb-1">Total EMIs Due</p>
                                            <p className="text-3xl font-bold text-blue-600">{emiBounceRateData.totalEMIsDue}</p>
                                            <p className="text-xs text-gray-500 mt-1">All EMIs in Period</p>
                                        </div>
                                        <FaFileInvoiceDollar className="text-blue-400 text-4xl opacity-50" />
                                    </div>
                                </div>

                                {/* Bounce Rate Card */}
                                <div className={`bg-gradient-to-br p-6 rounded-lg border-l-4 ${
                                    parseFloat(emiBounceRateData.bounceRatePercentage) < 5
                                        ? 'from-green-50 to-green-100 border-green-500'
                                        : parseFloat(emiBounceRateData.bounceRatePercentage) < 15
                                        ? 'from-yellow-50 to-yellow-100 border-yellow-500'
                                        : 'from-red-50 to-red-100 border-red-500'
                                }`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600 font-medium mb-1">Bounce Rate</p>
                                            <p className={`text-3xl font-bold ${
                                                parseFloat(emiBounceRateData.bounceRatePercentage) < 5
                                                    ? 'text-green-600'
                                                    : parseFloat(emiBounceRateData.bounceRatePercentage) < 15
                                                    ? 'text-yellow-600'
                                                    : 'text-red-600'
                                            }`}>
                                                {parseFloat(emiBounceRateData.bounceRatePercentage).toFixed(2)}%
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {parseFloat(emiBounceRateData.bounceRatePercentage) < 5
                                                    ? 'Excellent Collection'
                                                    : parseFloat(emiBounceRateData.bounceRatePercentage) < 15
                                                    ? 'Good Collection'
                                                    : 'Needs Attention'}
                                            </p>
                                        </div>
                                        {parseFloat(emiBounceRateData.bounceRatePercentage) < 5 ? (
                                            <FaArrowDown className="text-green-400 text-4xl opacity-50" />
                                        ) : (
                                            <FaArrowUp className="text-red-400 text-4xl opacity-50" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loans Disbursed per Branch */}
                <div className="px-6 pb-6">
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                        <h3 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                            <FaChartBar className="text-indigo-500" />
                            Loans Disbursed per Branch
                        </h3>
                        {branchDisbursementData ? (
                            <div className="h-96">
                                <Bar data={branchDisbursementData} options={branchChartOptions} />
                            </div>
                        ) : (
                            <div className="h-96 flex items-center justify-center text-gray-400">
                                <p>Loading chart data...</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinanceReports;
